import React, { useCallback, useContext, useMemo, useState } from 'react';
import moment from 'moment';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { PlaybackInformationContext } from '../../../../contexts/PlaybackInformationContext';

const formatTime = (seconds: number) => {
	return moment.utc(seconds * 1000).format('HH:mm:ss');
};

export const ProgressContainer: React.FC = () => {
	const { position: realPosition, setPosition, isCasting, isLive, duration, date: realDate } = useContext(PlaybackInformationContext);

	const [isDragging, setIsDragging] = useState(false);
	const [draggedPosition, setDraggedPosition] = useState(-1);

	const isCastingLive = useMemo(() => isCasting && isLive, [isCasting, isLive]);

	const date = useMemo(() => {
		if (!realDate || !isDragging) return realDate;

		return new Date(realDate.getTime() + (draggedPosition - realPosition) * 1000);
	}, [realDate, draggedPosition, isDragging, realPosition]);

	const position = useMemo(() => {
		return isDragging ? draggedPosition : realPosition;
	}, [draggedPosition, isDragging, realPosition]);

	const progressText = useMemo(() => {
		if (date) {
			return moment.utc(date.getTime()).format('HH:mm:ss');
		}

		if (isCastingLive) {
			const now = new Date().getTime();
			return moment.utc(now + realPosition).format('HH:mm:ss');
		}

		return formatTime(position);
	}, [date, isCastingLive, position, realPosition]);

	const endText = useMemo(() => {
		if (date || isCastingLive) {
			return 'LIVE';
		}

		return formatTime(duration);
	}, [date, duration, isCastingLive]);

	const onProgressClick = useCallback(
		(e) => {
			e.preventDefault();

			if (isLive && setPosition) {
				setPosition(duration);
			}
		},
		[duration, isLive, setPosition]
	);

	const onDragStart = useCallback((newDraggedPosition) => {
		setIsDragging(true);
		setDraggedPosition(newDraggedPosition);
	}, []);

	const onDraggedPositionChange = useCallback((newDraggedPosition) => {
		setDraggedPosition(newDraggedPosition);
	}, []);

	const onDragEnd = useCallback(() => {
		if (isDragging) {
			setPosition(draggedPosition);
			setIsDragging(false);
			setDraggedPosition(-1);
		}
	}, [draggedPosition, isDragging, setPosition]);

	const visible = !(isLive && duration < 300);

	return (
		<div className={`playback-information-progress-container${visible ? ' visible' : ''}`}>
			<div className={'playback-information-progressed-time'}>{progressText}</div>
			<ProgressBar
				onDragStart={onDragStart}
				onDragEnd={onDragEnd}
				isDragging={isDragging}
				onDraggedPositionChange={onDraggedPositionChange}
				position={position}
				duration={isCastingLive ? position : duration}
				setPosition={setPosition}
			/>
			<div role={'button'} className={`playback-information-duration${isLive ? ' live' : ''}`} onClick={onProgressClick}>
				{endText}
			</div>
		</div>
	);
};
